import { Stack } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import { useGridDataSourceWithRange } from "@entrilia/shared/components/grid/gridHooks";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import { downloadFileFromUrl } from "@entrilia/shared/services/downloadFile";
import { formatDate } from "@entrilia/shared/utilities/dateUtils";
import { formatNumber } from "@entrilia/shared/utilities/formatters";
import adminApi from "../../../../../api/adminApi";
import type { Category } from "../../../../../api/types/accessTypes";
import type { SearchInvestorDocumentsRequest } from "../../../../../api/types/fundStructureTypes";
import useClientContext from "../../../../../context/useClientContext";
import FundStructureDocumentFilters from "../../common/FundStructureDocumentFilters";
import {
  clearFiltersAction,
  getInitialFundStructureDocumentsFilterState,
  getSearchDocumentsFilterPayload,
} from "../../common/fundStructureDocumentsFilters";
import FundStructureDocumentsGrid from "../../common/FundStructureDocumentsGrid";

interface Props {
  fundId: string;
  fundName: string;
  categories: Category[];
}

const excludeGridColumns = ["fundNames", "views", "downloads", "totalViewTimeSeconds", "totalCompletionPercentage"];

const exportFundDocument = withErrorHandling(adminApi.exportFundDocumentsToExcel);
const getFundDocumentsDownloadToken = withErrorHandling(adminApi.getFundDocumentsDownloadToken);

const FundDocumentsTab = ({ fundId, fundName, categories }: Props) => {
  const { clientCode, hasPermissions } = useClientContext();
  const { sendNotification, sendNotificationError } = useNotificationContext();

  const [filterState, setFilterState] = useState(getInitialFundStructureDocumentsFilterState);
  const [isExporting, setIsExporting] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const filterPayload = useMemo(() => getSearchDocumentsFilterPayload(filterState), [filterState]);

  const getFundDocuments = useCallback(
    (req: SearchInvestorDocumentsRequest) => adminApi.searchFundDocuments(fundId, req),
    [fundId]
  );

  const { gridApiRef, dataSource, totalRows, isLoading, refreshDataSource } = useGridDataSourceWithRange(
    getFundDocuments,
    filterPayload
  );

  const getFundPermissions = useCallback(async () => adminApi.getFundPermissions(fundId), [fundId]);

  const [fundPermissions, fetchPermissionsError, { isFetching: isFetchingPermissions }] = useFetch(getFundPermissions);

  const categoryOptions = useMemo(
    () =>
      categories
        .filter((c) => c.type === "Message")
        .map(({ externalId, name }) => ({ value: externalId, label: name })),
    [categories]
  );

  const investorOptions = useMemo(
    () =>
      (fundPermissions?.permissions ?? []).map(({ fundInvestorId, investorName }) => ({
        value: fundInvestorId,
        label: investorName,
      })),
    [fundPermissions?.permissions]
  );

  if (fetchPermissionsError) {
    logError(fetchPermissionsError, "[FundDocumentsTab] getFundPermissions");
    return <DataLoadingFailed title="Loading fund documents failed" />;
  }

  const handleExportToExcel = async (withFilters: boolean) => {
    const payload: SearchInvestorDocumentsRequest = withFilters
      ? { range: {}, ...filterPayload }
      : { range: {}, publishingStatusFilter: "Any" };

    const fileName = `${fundName}_documents_${formatDate(new Date(), "yyyyMMdd_HHmmss")}.xlsx`;

    setIsExporting(true);
    const [resp, error] = await exportFundDocument(fundId, payload, fileName);
    setIsExporting(false);

    if (error) {
      sendNotificationError(error.message);
      logError(error, "[FundDocumentsTab] exportFundDocumentsToExcel");
      return;
    }

    await downloadFileFromUrl(resp.downloadUrl);

    if (resp.isTruncated) {
      sendNotification(
        `Exported first ${formatNumber(resp.exportedRecordsCount)} records. Please use filters to reduce the dataset and export additional records.`
      );
    }
  };

  const handleDownloadDocuments = async (withFilters: boolean) => {
    const payload: SearchInvestorDocumentsRequest = withFilters
      ? { range: {}, ...filterPayload }
      : { range: {}, publishingStatusFilter: "Any" };

    setIsDownloading(true);
    const [download, error] = await getFundDocumentsDownloadToken(fundId, payload);
    setIsDownloading(false);

    if (error) {
      sendNotificationError(error.message);
      logError(error, "[FundDocumentsTab] getFundDocumentsDownloadToken");
      return;
    }

    await downloadFileFromUrl(adminApi.createArchiveDownloadUrl(clientCode, download.token));

    if (download.isTruncated) {
      sendNotification(
        `Downloaded first ${formatNumber(download.documentCount)} documents. Please use filters to reduce the dataset and download additional documents.`
      );
    }
  };

  const handleClearFilters = () => {
    setFilterState(clearFiltersAction());
  };

  const canDownloadDocuments = hasPermissions(["ViewDocumentCollections"]);

  return (
    <Stack spacing={2.5} sx={{ height: "100%" }}>
      <FundStructureDocumentFilters
        categoryOptions={categoryOptions}
        investorOptions={investorOptions}
        filterState={filterState}
        onChange={setFilterState}
        onClear={handleClearFilters}
        isLoading={isFetchingPermissions || isLoading}
        recordsCount={totalRows ?? 0}
        onExportAllRecords={() => handleExportToExcel(false)}
        onExportFilteredRecords={() => handleExportToExcel(true)}
        isExporting={isExporting}
        onDownloadAllDocuments={canDownloadDocuments ? () => handleDownloadDocuments(false) : undefined}
        onDownloadFilteredDocuments={canDownloadDocuments ? () => handleDownloadDocuments(true) : undefined}
        isDownloading={isDownloading}
      />
      <FundStructureDocumentsGrid
        dataSource={dataSource}
        lazyLoading
        categories={categories}
        excludeColumns={excludeGridColumns}
        onRefreshDocuments={refreshDataSource}
        apiRef={gridApiRef}
        allowStatusChanges={hasPermissions(["ManageDocumentCollections"])}
      />
    </Stack>
  );
};

export default FundDocumentsTab;
